import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { roleOf } from "@/lib/roles";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ListToolbar } from "@/components/ListToolbar";
import { Plus, GraduationCap, ArrowRight, User } from "lucide-react";

export default function Supervisions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = roleOf(user);
  const canAllocate = ["tenant_admin", "staff", "super_admin"].includes(role);
  const [sups, setSups] = useState([]);
  const [students, setStudents] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: "", supervisor_id: "", programme: "", stage: "Proposal" });
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("all");

  const load = () => api.get("/supervisions").then((r) => setSups(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (canAllocate) {
      api.get("/students").then((r) => setStudents(r.data)).catch(() => {});
      api.get("/supervisors").then((r) => setSupervisors(r.data)).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    try {
      await api.post("/supervisions", form);
      toast.success("Supervision allocated");
      setOpen(false);
      setForm({ student_id: "", supervisor_id: "", programme: "", stage: "Proposal" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Research Supervision</h1>
          <p className="text-muted-foreground">Version-controlled records, chapters, feedback & milestones.</p>
        </div>
        {canAllocate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="allocate-btn" className="brand-bg text-white hover:opacity-90"><Plus className="h-4 w-4 mr-1" /> Allocate Supervisor</Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle>Allocate Supervisor</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Student</Label>
                  <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                    <SelectTrigger data-testid="sel-student"><SelectValue placeholder="Select student" /></SelectTrigger>
                    <SelectContent className="bg-white">{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Supervisor (with active load)</Label>
                  <Select value={form.supervisor_id} onValueChange={(v) => setForm({ ...form, supervisor_id: v })}>
                    <SelectTrigger data-testid="sel-supervisor"><SelectValue placeholder="Select supervisor" /></SelectTrigger>
                    <SelectContent className="bg-white">{supervisors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.active_load} active · {s.expertise}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Programme</Label>
                  <input className="w-full border border-input rounded-md px-3 py-2 text-sm" data-testid="sup-programme" value={form.programme} onChange={(e) => setForm({ ...form, programme: e.target.value })} />
                </div>
              </div>
              <DialogFooter><Button data-testid="sup-create" onClick={create} disabled={!form.student_id || !form.supervisor_id || !form.programme.trim()} className="brand-bg text-white">Allocate</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-3" data-testid="supervisions-list">
        {sups.length > 0 && (
          <div className="md:col-span-2">
            <ListToolbar
              testid="supervision"
              search={q}
              onSearch={setQ}
              placeholder="Search by student, supervisor or programme…"
              filters={[{
                key: "stage", value: stageFilter, onChange: setStageFilter, label: "Stage",
                options: [{ value: "all", label: "All stages" }, ...Array.from(new Set(sups.map((s) => s.stage).filter(Boolean))).map((st) => ({ value: st, label: st }))],
              }]}
            />
          </div>
        )}
        {sups
          .filter((s) => stageFilter === "all" || s.stage === stageFilter)
          .filter((s) => {
            const t = q.trim().toLowerCase();
            return !t || [s.student_name, s.supervisor_name, s.programme].some((x) => (x || "").toLowerCase().includes(t));
          })
          .map((s) => (
          <Card key={s.id} className="p-5 hover:shadow-sm cursor-pointer group" data-testid={`sup-${s.id}`} onClick={() => navigate(`/supervisions/${s.id}`)}>
            <div className="flex items-start justify-between">
              <div className="h-10 w-10 rounded-md bg-secondary flex items-center justify-center brand-text"><GraduationCap className="h-5 w-5" /></div>
              <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
            </div>
            <h3 className="font-heading font-semibold mt-3">{s.student_name}</h3>
            <p className="text-sm text-muted-foreground flex items-center gap-1"><User className="h-3 w-3" /> {s.supervisor_name}</p>
            <div className="flex items-center gap-2 mt-3">
              <Badge variant="outline">{s.programme || "Programme not set"}</Badge>
              <Badge className="brand-bg text-white">{s.stage}</Badge>
            </div>
          </Card>
        ))}
        {sups.length === 0 && <p className="text-muted-foreground text-sm">No supervision records yet.</p>}
      </div>
    </div>
  );
}
